import { useQuery } from '@tanstack/react-query';
import { useEffect } from 'react';
import { Announcement, AuthNonceHeader, ProfileAnnouncements } from '@/api/spec';
import { useAnnouncementsStore } from '@/feature/announcements';
import useAppStore from '../store/rootStore';
import { getValidNonce } from '../store/slices/authSlice';
import { environment } from '../environment';

interface AnnouncementsResponse {
  active: Announcement[];
}

const fetchAnnouncements = async (authNonce?: string): Promise<Announcement[]> => {
  const headers: HeadersInit = {};
  if (authNonce) {
    headers[AuthNonceHeader] = authNonce;
  }

  const response = await fetch(`${environment.apiUrl}${ProfileAnnouncements}`, {
    headers,
  });

  if (!response.ok) {
    const error: Error & { response?: { status: number } } = new Error(`Failed to fetch announcements: ${response.status}`);
    error.response = { status: response.status };
    throw error;
  }

  const data: AnnouncementsResponse = await response.json();
  return data.active;
};

export const useAnnouncementsPolling = () => {
  const validNonceValue = useAppStore((state) => getValidNonce(state.authNonce)?.value);
  const setAnnouncements = useAnnouncementsStore((state) => state.setAnnouncements);

  const { data } = useQuery({
    queryKey: ['announcements', validNonceValue],
    queryFn: () => fetchAnnouncements(validNonceValue),
    refetchInterval: 5 * 60_000,
    staleTime: 60_000,
  });

  useEffect(() => {
    if (data) {
      setAnnouncements(data);
    }
  }, [data, setAnnouncements]);
};
